import React, { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { submitPaymentIssue } from '@services/subscriptionService';

const ISSUE_TYPES = [
  'Card declined',
  'Duplicate charge',
  'Wrong amount',
  'Card update failed',
  'Unrecognized charge',
  'Other',
];

interface RaisePaymentIssueDialogProps {
  open: boolean;
  onClose: () => void;
  gateway?: string;
}

const RaisePaymentIssueDialog: React.FC<RaisePaymentIssueDialogProps> = ({ open, onClose, gateway }) => {
  const [issueType, setIssueType] = useState(ISSUE_TYPES[0]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState('');

  const submit = async () => {
    if (!description.trim()) {
      setError('Describe the payment issue.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const issue = await submitPaymentIssue({
        issueType,
        description: description.trim(),
        gateway,
      });
      setConfirmation(`Issue submitted (${issue.id.slice(0, 8)}). Our team will follow up shortly.`);
      setDescription('');
    } catch (err: unknown) {
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setError(anyErr.response?.data?.message || anyErr.message || 'Could not submit issue');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setConfirmation('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 800, color: brandColors.navy }}>
        Raise issue
      </DialogTitle>
      <DialogContent>
        {confirmation ? (
          <Alert severity="success">{confirmation}</Alert>
        ) : (
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            <Typography variant="body2" sx={{ color: brandColors.slate }}>
              Tell us about a card or charge problem. We keep a record and follow up from support.
            </Typography>
            <TextField
              select
              label="Issue type"
              value={issueType}
              onChange={(e) => setIssueType(e.target.value)}
              size="small"
              fullWidth
            >
              {ISSUE_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={4}
              fullWidth
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: 'none', fontWeight: 700, color: brandColors.slate }}>
          {confirmation ? 'Done' : 'Cancel'}
        </Button>
        {!confirmation && (
          <Button
            variant="contained"
            disabled={saving}
            onClick={submit}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: brandColors.navy,
              '&:hover': { bgcolor: '#0a0f28' },
            }}
          >
            {saving ? 'Submitting…' : 'Submit'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RaisePaymentIssueDialog;
